/**
 * Services & capabilities (design 4A–C): one catalogue for both provenances,
 * default duration always said to be a suggestion, ONE status control,
 * capability tables that do one thing. Name/description edit is gap-gated
 * (ZOY-242 catalogue-identity decision) — never silently invented.
 */

import { useMemo, useState } from 'react';
import { parseNumericInput } from '@/core/i18n/digits';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Chip, Notice, TextField } from '@/ui/kit';
import { Column, Pager, PrimaryCell, RowButton, TableSearch, WorkTable } from '@/ui/WorkTable';
import {
  ConfirmDialog,
  FieldGrid,
  FormCard,
  SelectField,
  Span2,
  TextAreaField,
  Toggle,
} from '@/ui/formkit';
import { useNumberedList } from '@/core/api/usePagedList';
import { useDebounced } from '@/core/api/useDebounced';
import type { QueryFilter } from '@/core/api/querySpec';
import {
  addPractitionerCapability,
  listPersonnel,
  createService,
  listPractitionerCapabilities,
  listServices,
  patchServiceSettings,
  removePractitionerCapability,
  setServiceDisabled,
  WORKFLOW_TYPES,
  modesOf,
  type DeliveryMode,
  type ServiceView,
  type WorkflowType,
} from '@/core/clinic/setupApi';
import { ModeSetField, modesLabel } from './ModeSetField';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { PERM } from '@/core/authz/permissions';
import { formatMoney, storageUnit } from '@/core/clinic/money';
import { useLocale } from '@/core/i18n/LocaleProvider';
import { useClinicSettings } from '../patients/useClinicSettings';

type View = { kind: 'list' } | { kind: 'create' } | { kind: 'detail'; service: ServiceView };

export function ServicesTab() {
  const intl = useIntl();
  const { locale } = useLocale();
  const currency = useClinicSettings()?.currencyCode ?? null;
  const t = (id: string) => intl.formatMessage({ id });
  const authz = useAuthz();
  const manage = authz.can(PERM.catalogManage);
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>({ kind: 'list' });
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 300);

  const filters = useMemo<QueryFilter[]>(
    () =>
      debounced.trim() === '' ? [] : [{ field: 'name', op: 'contains', value: debounced.trim() }],
    [debounced],
  );

  const list = useNumberedList<ServiceView>({
    keyBase: ['services', authz.clinicId],
    fetchPage: (spec) => listServices(spec),
    filters,
    sort: [{ field: 'name', dir: 'asc' }],
  });

  const toggle = useMutation({
    mutationFn: (s: ServiceView) => setServiceDisabled(s.id, !s.disabled),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['services'] }),
  });

  if (view.kind === 'create') {
    return (
      <ServiceCreate
        currency={currency}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: ['services'] });
          setView({ kind: 'list' });
        }}
        onCancel={() => setView({ kind: 'list' })}
      />
    );
  }
  if (view.kind === 'detail') {
    return (
      <ServiceDetail
        service={view.service}
        currency={currency}
        onBack={() => setView({ kind: 'list' })}
      />
    );
  }

  // ZOY-406: the ONE money convention (core/clinic/money.ts) — IRR displays
  // as Toman with its unit, any other currency as itself; null stays '—'.
  const money = (n: number | null) => formatMoney(n, locale, currency);

  const columns: Column[] = [
    { id: 'service', label: t('svc.colService') as string, width: '1.8fr' },
    { id: 'location', label: t('svc.colLocation') as string, width: '1fr', tier: 'desktop-only' },
    { id: 'price', label: t('svc.colPrice') as string, width: 'auto', tier: 'desktop-only' },
    { id: 'duration', label: t('svc.colDuration') as string, width: 'auto', tier: 'tablet-up' },
    { id: 'status', label: t('svc.colStatus') as string, width: 'auto', tier: 'tablet-up' },
    { id: 'actions', label: t('staff.colActions') as string, width: 'auto', align: 'end' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <p className={a.pageSub} style={{ margin: 0 }}>
          {t('svc.sub')}
        </p>
        {manage && (
          <Button variant="primary" onClick={() => setView({ kind: 'create' })}>
            {t('svc.add')}
          </Button>
        )}
      </div>

      <WorkTable<ServiceView>
        columns={columns}
        rows={list.view.rows}
        rowKey={(s) => s.id}
        loading={list.loading}
        error={list.error !== null}
        onRetry={list.refetch}
        toolbar={
          <TableSearch
            value={search}
            onChange={setSearch}
            placeholder={t('svc.colService') as string}
          />
        }
        empty={{
          title: t(debounced ? 'table.noResultsTitle' : 'svc.emptyTitle') as string,
          body: t(debounced ? 'table.noResultsBody' : 'svc.emptyBody') as string,
        }}
        renderCell={(svc, col) => {
          switch (col) {
            case 'service':
              return (
                <PrimaryCell
                  main={svc.name}
                  sub={
                    <>
                      {svc.source === 'platform' ? t('svc.platform') : t('svc.clinicMade')} ·{' '}
                      {workflowLabel(t, svc.workflowType)}
                    </>
                  }
                />
              );
            case 'location':
              return modesLabel(t, modesOf(svc));
            case 'price':
              return <span className="zc-tabular">{money(svc.price)}</span>;
            case 'duration':
              return (
                <span>
                  <span className="zc-tabular">{intl.formatNumber(svc.durationMinutes)}</span>{' '}
                  {t('common.minutes')}
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--zc-faint)' }}>
                    {t('svc.durationNote')}
                  </span>
                </span>
              );
            case 'status':
              return svc.disabled ? (
                <Chip kind="quiet">{t('svc.disabledHere')}</Chip>
              ) : (
                <Chip kind="ok">{t('common.enabled')}</Chip>
              );
            case 'actions':
              return (
                <span style={{ display: 'flex', gap: 8 }}>
                  <RowButton onClick={() => setView({ kind: 'detail', service: svc })}>
                    {t('common.view')}
                  </RowButton>
                  {manage && (
                    <RowButton danger={!svc.disabled} onClick={() => toggle.mutate(svc)}>
                      {svc.disabled ? t('common.enable') : t('common.disable')}
                    </RowButton>
                  )}
                </span>
              );
            default:
              return null;
          }
        }}
        footer={
          <Pager
            view={list.view}
            perPage={list.perPage}
            onPerPage={list.setPerPage}
            onPage={list.goTo}
          />
        }
      />
      <Notice kind="info">{t('svc.oneSwitchNote')}</Notice>
    </div>
  );
}

/** ZOY-550: the standardized meaning, or the honest legacy state — never a blank. */
function workflowLabel(t: (id: string) => React.ReactNode, w: string | null): React.ReactNode {
  if (w === null) return t('svc.wf.unclassified');
  return (WORKFLOW_TYPES as readonly string[]).includes(w) ? t(`svc.wf.${w}`) : w;
}

export function ServiceCreate(props: {
  currency: string | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [duration, setDuration] = useState('30');
  const [modes, setModes] = useState<DeliveryMode[]>(['in_clinic']);
  const [capacity, setCapacity] = useState('');
  // ZOY-542/550: required, chosen ONCE — no edit surface exists afterwards.
  const [workflowType, setWorkflowType] = useState<WorkflowType | ''>('');
  const [error, setError] = useState<string | null>(null);
  // ZOY-685: a group service is in clinic or online — never at home; an
  // off-site tick made before the workflow was chosen is dropped.
  const group = workflowType === 'group_session';
  const effectiveModes = group ? modes.filter((m) => m !== 'off_site') : modes;

  const create = useMutation({
    mutationFn: () =>
      createService({
        name: name.trim(),
        workflowType: workflowType as WorkflowType,
        description: description.trim() || undefined,
        price: parseNumericInput(price) ?? undefined,
        durationMinutes: (parseNumericInput(duration) ?? 0) || 30,
        modes: effectiveModes,
        defaultGroupCapacity: group ? (parseNumericInput(capacity) ?? undefined) : undefined,
      }),
    onSuccess: props.onDone,
    // A refusal must never be silent (the pre-ZOY-550 create had no error path).
    onError: () => setError(t('svc.createFailed') as string),
  });

  return (
    <div style={{ maxInlineSize: 680, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {error && (
        <Notice kind="err" role="alert">
          {error}
        </Notice>
      )}
      <FormCard
        title={t('svc.add')}
        onSubmit={() => create.mutate()}
        cta={t('svc.createCta')}
        ctaDisabled={name.trim() === '' || workflowType === '' || effectiveModes.length === 0}
        ctaDisabledReason={
          name.trim() !== '' && workflowType === ''
            ? (t('svc.workflowNeeded') as string)
            : undefined
        }
        busy={create.isPending}
        secondary={{ label: t('common.cancel'), onClick: props.onCancel }}
      >
        <FieldGrid>
          <Span2>
            <TextField
              label={t('svc.name')}
              value={name}
              onChange={setName}
              requirement={{ word: t('signin.required'), kind: 'required' }}
            />
          </Span2>
          <Span2>
            <SelectField
              label={t('svc.workflow') as string}
              value={workflowType}
              onChange={(v) => setWorkflowType(v as WorkflowType | '')}
              help={t('svc.workflowHelp') as string}
              requirement={{ word: t('signin.required') as string, kind: 'required' }}
              options={[
                { value: '', label: '—' },
                ...WORKFLOW_TYPES.map((w) => ({ value: w, label: t(`svc.wf.${w}`) as string })),
              ]}
            />
          </Span2>
          <Span2>
            <TextAreaField
              label={t('svc.description')}
              value={description}
              onChange={setDescription}
              help={t('svc.descriptionHelp')}
              requirement={{ word: t('signin.optional'), kind: 'optional' }}
              rows={2}
            />
          </Span2>
          <TextField
            label={t('svc.price')}
            value={price}
            onChange={setPrice}
            help={t('svc.priceHelp')}
            prefix={storageUnit(props.currency)}
            requirement={{ word: t('signin.optional'), kind: 'optional' }}
            inputMode="numeric"
            machine
          />
          <TextField
            label={t('svc.duration')}
            value={duration}
            onChange={setDuration}
            help={t('svc.durationHelp')}
            inputMode="numeric"
            machine
          />
          <Span2>
            <ModeSetField
              label={t('svc.location') as string}
              value={effectiveModes}
              onChange={setModes}
              choices={group ? ['in_clinic', 'online'] : undefined}
              help={t('svc.locationHelp') as string}
              error={effectiveModes.length === 0 ? (t('svc.modesNeeded') as string) : undefined}
            />
          </Span2>
          {group && (
            <>
              <TextField
                label={t('svc.groupCapacity')}
                value={capacity}
                onChange={setCapacity}
                help={t('svc.groupCapacityHelp')}
                requirement={{ word: t('signin.optional'), kind: 'optional' }}
                inputMode="numeric"
                machine
              />
              <Span2>
                <Notice kind="info">{t('svc.groupNote')}</Notice>
              </Span2>
            </>
          )}
        </FieldGrid>
      </FormCard>
    </div>
  );
}

function ServiceDetail(props: {
  service: ServiceView;
  currency: string | null;
  onBack: () => void;
}) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const svc = props.service;
  const authz = useAuthz();
  const manage = authz.can(PERM.catalogManage);
  const capManage = authz.can(PERM.capabilitiesManage);
  const queryClient = useQueryClient();

  const [price, setPrice] = useState(svc.price === null ? '' : String(svc.price));
  const [duration, setDuration] = useState(String(svc.durationMinutes));
  const [modes, setModes] = useState<DeliveryMode[]>(modesOf(svc));
  const [capacity, setCapacity] = useState(
    svc.defaultGroupCapacity == null ? '' : String(svc.defaultGroupCapacity),
  );
  const group = svc.workflowType === 'group_session';
  const [enabled, setEnabled] = useState(!svc.disabled);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      await patchServiceSettings(svc.id, {
        price: parseNumericInput(price),
        durationMinutes: (parseNumericInput(duration) ?? 0) || svc.durationMinutes,
        modes,
        ...(group ? { defaultGroupCapacity: parseNumericInput(capacity) ?? null } : {}),
      });
      if (svc.disabled === enabled) await setServiceDisabled(svc.id, !enabled);
    },
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      void queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });

  return (
    <div style={{ maxInlineSize: 760, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2 style={{ fontSize: 19, fontWeight: 700, color: 'var(--zc-teal-deep)' }}>
            {svc.name}
          </h2>
          <span style={{ fontSize: 12.5, color: 'var(--zc-muted)' }}>
            {svc.source === 'platform' ? t('svc.platform') : t('svc.clinicMade')}
            {' · '}
            {workflowLabel(t, svc.workflowType)}
            {svc.description ? ` · ${svc.description}` : ''}
          </span>
        </div>
        <Button variant="quiet" onClick={props.onBack}>
          {t('common.back')}
        </Button>
      </div>

      {/* ZOY-242: identity editing gap-gated, honestly. */}
      <Notice kind="warn">{t('svc.editGapNote')}</Notice>

      {manage && (
        <FormCard
          title={t('profile.cardConfig') as string}
          onSubmit={() => save.mutate()}
          cta={t('common.save')}
          busy={save.isPending}
          ctaDisabled={modes.length === 0}
          footNote={saved ? (t('common.saved') as string) : (t('svc.oneSwitchNote') as string)}
        >
          <FieldGrid>
            <TextField
              label={t('svc.price')}
              value={price}
              onChange={setPrice}
              help={t('svc.priceHelp')}
              prefix={storageUnit(props.currency)}
              inputMode="numeric"
              machine
            />
            <TextField
              label={t('svc.duration')}
              value={duration}
              onChange={setDuration}
              help={t('svc.durationHelp')}
              inputMode="numeric"
              machine
            />
            <Span2>
              <ModeSetField
                label={t('svc.location') as string}
                value={modes}
                onChange={setModes}
                choices={group ? ['in_clinic', 'online'] : undefined}
                help={t('svc.locationHelp') as string}
                error={modes.length === 0 ? (t('svc.modesNeeded') as string) : undefined}
              />
            </Span2>
            {group && (
              <TextField
                label={t('svc.groupCapacity')}
                value={capacity}
                onChange={setCapacity}
                help={t('svc.groupCapacityHelp')}
                inputMode="numeric"
                machine
              />
            )}
            <Toggle
              label={t('common.enabled')}
              help={t('svc.oneSwitchNote')}
              checked={enabled}
              onChange={setEnabled}
              stateWord={
                enabled ? (t('common.enabled') as string) : (t('common.disabled') as string)
              }
            />
          </FieldGrid>
        </FormCard>
      )}

      <ServicePractitioners serviceId={svc.id} canManage={capManage} />
    </div>
  );
}

function ServicePractitioners({ serviceId, canManage }: { serviceId: string; canManage: boolean }) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const authz = useAuthz();
  const clinicId = authz.clinicId ?? '';
  const queryClient = useQueryClient();
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [pickUid, setPickUid] = useState('');

  const capsQuery = useQuery({
    queryKey: ['svc-caps', serviceId],
    queryFn: () =>
      listPractitionerCapabilities({
        filters: [{ field: 'serviceId', op: 'eq', value: serviceId }],
        limit: 100,
      }),
  });
  // ZOY-363: capability holders and grant candidates are PERSONNEL —
  // displayName comes with the clinic's own list, no Auth member read.
  const personnelQuery = useQuery({
    queryKey: ['personnel', 'all', clinicId],
    queryFn: () => listPersonnel({ limit: 100, sort: [{ field: 'uid', dir: 'asc' }] }),
    staleTime: 5 * 60_000,
  });
  const personnel = personnelQuery.data?.items ?? [];

  const caps = capsQuery.data?.items ?? [];
  const personName = (uid: string) => {
    const p = personnel.find((x) => x.uid === uid);
    return (
      p?.displayName ??
      (p?.jobTitle ? `${t('staff.unnamed')} · ${p.jobTitle}` : (t('staff.unnamed') as string))
    );
  };
  const capUids = new Set(caps.map((c) => c.uid));
  const addable = personnel.filter((p) => p.practitioner !== null && !capUids.has(p.uid));

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['svc-caps', serviceId] });
  const grant = useMutation({
    mutationFn: (uid: string) => addPractitionerCapability({ uid, serviceId }),
    onSuccess: () => {
      setPickUid('');
      invalidate();
    },
  });
  const revoke = useMutation({
    mutationFn: removePractitionerCapability,
    onSuccess: () => {
      setRemoveId(null);
      invalidate();
    },
  });

  return (
    <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
      <div style={{ padding: '14px 18px 4px' }}>
        <strong style={{ color: 'var(--zc-teal-deep)' }}>{t('svc.practitioners')}</strong>
        <p style={{ fontSize: 12.5, color: 'var(--zc-muted)', marginBlockStart: 4 }}>
          {t('svc.practitionersSub')}
        </p>
      </div>
      {caps.map((c) => (
        <div key={c.id} className={a.row}>
          <span className={a.rowKey} style={{ minInlineSize: 160 }}>
            {personName(c.uid)}
          </span>
          <span className={a.rowValue} style={{ fontSize: 12.5 }}>
            {c.isPreferred && <Chip kind="ok">{t('svc.preferred')}</Chip>}{' '}
            {c.matchScore !== null && (
              <Chip kind="quiet">{t('svc.match', { n: intl.formatNumber(c.matchScore) })}</Chip>
            )}{' '}
            <span style={{ color: 'var(--zc-faint)' }}>{t('svc.rankNote')}</span>
          </span>
          {canManage && (
            <span className={a.rowAction}>
              <Button variant="link" onClick={() => setRemoveId(c.id)}>
                {t('common.remove')}
              </Button>
            </span>
          )}
        </div>
      ))}
      {caps.length === 0 && (
        <div className={a.row}>
          <span className={a.rowValue} style={{ color: 'var(--zc-faint)' }}>
            —
          </span>
        </div>
      )}
      {canManage && (
        <div className={a.row}>
          <span className={a.rowValue} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <SelectField
              label={t('svc.grant')}
              value={pickUid}
              onChange={setPickUid}
              options={[
                { value: '', label: '—' },
                ...addable.map((p) => ({ value: p.uid, label: personName(p.uid) })),
              ]}
            />
            <Button
              variant="quiet"
              disabled={pickUid === '' || grant.isPending}
              onClick={() => pickUid && grant.mutate(pickUid)}
            >
              {t('common.add')}
            </Button>
          </span>
        </div>
      )}
      <ConfirmDialog
        open={removeId !== null}
        title={t('svc.removeCapTitle') as string}
        body={t('svc.removeCapBody')}
        confirmLabel={t('common.remove') as string}
        danger
        busy={revoke.isPending}
        onConfirm={() => removeId && revoke.mutate(removeId)}
        onClose={() => setRemoveId(null)}
      />
    </div>
  );
}
